<script setup lang="ts">
//#region imports
import { computed, nextTick, ref, useTemplateRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { format } from 'date-fns';
import { useTypedStore } from '@/store';
import { useApi } from '@/api';
import { useFormatCurrency } from '@/composables/useFormatCurrency';
import { useLodash } from '@/setup/lodash';
import { useDashboardFilter } from '@/composables/useDashboardFilter';
import { ADD_DASHBOARD_IS_LOADING } from '@/store/action_types/dashboards';
import { usePopupManager } from '@/components/PopupManager.vue';
import SelectMetrics from '@/views/Dashboard/components/SelectMetrics.vue';
import DashboardMetricStat from '@/views/Dashboard/components/DashboardMetricStat.vue';
import DashboardMetricChart from '@/views/Dashboard/components/DashboardMetricChart.vue';
import DashboardMetricTable from '@/views/Dashboard/components/DashboardMetricTable.vue';
import DashboardFilter, { type CurrentFilter } from '@/views/Dashboard/components/DashboardFilter.vue';

import {
  type DateRange,
  type EmitsToListeners,
  type FilterRecord,
  isErrorResponse,
  typeGuard,
} from '@/types/core';
import DashboardSeries from '@/views/Dashboard/components/DashboardSeries.vue';
import type {
  ChartVisualizationType, DashboardMetricTypes,
  DashboardPanel,
  DashboardPanelBase, DashboardPanelChart, DashboardPanelStat,
  DashboardPanelTable,
  DashboardPanelTypes,
  DashboardsRequestSeries,
  FilterRecordExtended,
} from '@/types/dashboard';
import type {
  PostDashboardsStatsData,
  PostDashboardsStatsResponse,
} from '@/types/openapi-ts';

//#endregion

//#region types
interface Props {
  isRemoved?: boolean,
  period?: DateRange,
  propCurrencyCode?: string,
  data?: DashboardPanel,
  popupType?: string,
  rowIndex: number,
  index?: number,
}

interface  Emits {
  'close': [],
  'success': [popupType: string, rowIndex: number, dashboardStatComputed: DashboardPanel, indexEditMetric: number],
}

export interface AddDashboardMetricsParams {
  data?: Props;
  listeners?: EmitsToListeners<Emits>
}

export interface DashboardFormErrors {
  panel?: {
    name?: string;
    series?: string
  }
}

export interface Grouping {
  value: string;
  label: string;
}
//#endregion

//#region define
const props = withDefaults(defineProps<Props>(), {
  isRemoved: false,
  popupType: 'add',
  period: () => [],
});
const emit = defineEmits<Emits>();
//#endregion

//#region const
const allDimensionSelect = [
  'affiliate_email',
  'sub_affiliate_email',
  'player_country_name',
  'player_region_name',
  'payment_system_name',
];

const updaters = {
  stat: updateStat,
  'chart-line': updateChart,
  'chart-pie': updateChart,
  'chart-bar': updateChart,
  table: updateTable,
};

const selectCountValue = [3, 5, 10, 15, 20];
//#endregion

//#region composables
const store = useTypedStore();
const { t } = useI18n();
const api = useApi();
const {
  currencyCode,
  formatMetric,
} = useFormatCurrency();
const _ = useLodash();
const {
  handleDisabled,
  isDisabled,
  parseFilter,
  transformFromFilterView,
  decodeFilterSelect,
  transformForFilterView,
} = useDashboardFilter();
//#endregion

//#region state
const isVisible = ref(false);
const errors = ref<DashboardFormErrors>({});

const panelName = ref<string>();
const filterSelect = ref({} as FilterRecordExtended);
const filterTab = ref<FilterRecord>({});
const animation = ref(true);
const isDisableSelectFilter = ref(false);

const selectedMetric = ref('');
const countQuery = ref(0);
const groupByGrouping = ref<string>();
const groupingCount = ref(5);
const groupingOther = ref(false);
const typeMetric = ref<DashboardMetricTypes>('stat');
const enableComparison = ref(true);
const prevChartType = ref<ChartVisualizationType>('line');
const actionsDisabled = ref(true);
const initFiltersDashboard = ref<CurrentFilter[]>([]);

const chartRef = useTemplateRef('chart');
const statRef = useTemplateRef('stat');
const tableRef = useTemplateRef('table');

const indexEditMetric = ref<number>();
const loading = ref(false);
const disabledGroupingSwitch = ref(false);
const groupingSwitch = ref(false);
const forecastValue = ref(false);
const series = ref<DashboardsRequestSeries>([]);
const report = ref<PostDashboardsStatsResponse['payload']>({} as PostDashboardsStatsResponse['payload']);
//#endregion

//#region computed
const reportsSettings = computed(() => store.getters['reports/reportsSettings']);
const stateDashboardFilter = computed(() => store.state.dashboards.stateDashboardFilter);
const addDashboardIsLoading = computed(() => store.state.dashboards.addDashbordIsLoading);

const resultCount = computed(() => {
  return selectCountValue.map((count) => ({
    id: count,
    name: `${t('dashboards.grouping.top')} ${count}`,
  }));
});

const dashboardStatComputed = computed<DashboardPanel>(() => {
  // TODO: изучить и исправить это приведение в будущем
  return {
    ...paramsData.value.panel as unknown as DashboardPanelBase,
    report: report.value,
  };
});

const paramsData = computed<PostDashboardsStatsData['body']>(() => {
  const filtersPanel = {
    ...filterSelect.value,
    search: {
      ...filterTab.value,
    },
  } as PostDashboardsStatsData['body']['panel']['filters'];

  const _period = {
    from: format(props.period[0] as Date, 'yyyy-MM-dd HH:mm:ss'),
    to: format(props.period[1] as Date, 'yyyy-MM-dd HH:mm:ss'),
  };

  let panel;
  if (typeMetric.value === 'stat') {
    panel = {
      name: setNamePanel.value,
      type: typeMetric.value,
      metric_name: selectedMetric.value as PostDashboardsStatsData['body']['panel']['metric_name'],
      filters: filtersPanel,
      enable_comparison: enableComparison.value,
    } as PostDashboardsStatsData['body']['panel'];
  } else if (typeMetric.value === 'table') {
    panel = {
      name: setNamePanel.value,
      type: typeMetric.value,
      filters: filtersPanel,
      metric_name: selectedMetric.value as PostDashboardsStatsData['body']['panel']['metric_name'],
      // свойства comparison_period свойства нет в api
      //comparison_period: comparedType.value,
      dimension_name: groupingSwitch.value ? groupByGrouping.value as PostDashboardsStatsData['body']['panel']['dimension_name'] : null,
      limit: groupingSwitch.value ? groupingCount.value : null,
      group_by_add_others: groupingSwitch.value ? groupingOther.value : false,
      enable_comparison: enableComparison.value,
    } as PostDashboardsStatsData['body']['panel'];
  } else {
    panel = {
      name: setNamePanel.value,
      type: 'chart',
      filters: filtersPanel,
      //@ts-ignore такого свойства нет в api, но логически оно нужно поэтому оставляем
      chart_visualization: chartType.value,
      enable_forecast: forecastValue.value,
      enable_comparison: enableComparison.value,

      series_group_by_dimension_name: groupingSwitch.value
        ? groupByGrouping.value
        : null,
      series_group_by_metric_name: groupingSwitch.value
        ? selectedMetric.value
        : null,
      series_group_by_limit: groupingSwitch.value
        ? groupingCount.value
        : null,
      series_group_by_add_others: groupingSwitch.value
        ? groupingOther.value
        : null,
      series: groupingSwitch.value ? [] : computedSeries.value,
    };
  }

  return {
    panel,
    filters: stateDashboardFilter.value,
    ..._period,
    currency_code: currencyCode.value,
  } as PostDashboardsStatsData['body'];
});

const computedSeries = computed<DashboardsRequestSeries>(() => {
  if (series.value.length === 0) {
    return [
      {
        name: t('dashboards.dashboards_filters.name_series'),
        metric_name: selectedMetric.value,
        y_axis: 'left',
        filters: {},
      },
    ] as DashboardsRequestSeries;
  }

  return series.value;
});

const chartType = computed<ChartVisualizationType>(() => {
  switch (typeMetric.value) {
    case 'chart-line':
      return 'line';
    case 'chart-bar':
      return 'bar';
    case 'chart-pie':
      return 'pie';
    default:
      return 'line';
  }
});

const metricList = computed(() => {
  return [
    {
      value: 'stat',
      name: t('dashboards.popup_column.card'),
      disabled: props.popupType !== 'add',
    },
    {
      value: 'chart-line',
      name: t('dashboards.type_chart.line'),
      disabled: props.popupType === 'edit' && (typeMetric.value === 'stat' || typeMetric.value === 'table'),
    },
    {
      value: 'chart-bar',
      name: t('dashboards.type_chart.bar'),
      disabled: props.popupType === 'edit' && (typeMetric.value === 'stat' || typeMetric.value === 'table'),
    },
    {
      value: 'chart-pie',
      name: t('dashboards.type_chart.pie'),
      disabled: !groupsAll.value.length || (props.popupType === 'edit' && (typeMetric.value === 'stat' || typeMetric.value === 'table')),
    },
    {
      value: 'table',
      name: t('dashboards.popup_column.table'),
      disabled: props.popupType !== 'add',
    },
  ];
});

const setNamePanel = computed<string>(() => {
  return (
    panelName.value ?? getTranslateMetrics(selectedMetric.value).title
  );
});

const groupsAll = computed(() => {
  return allDimensionSelect
    .filter((dim) => supportedDimenison.value.includes(dim as typeof supportedDimenison.value[number]))
    .map((dim) => ({
      value: dim,
      label: t(`dashboards.grouping.${dim}`),
    }));
});

const supportedDimenison = computed(() => {
  return (reportsSettings.value.dimensions || []).map((dim) => dim.column_name);
});

const columnGroup = computed(() => {
  return reportsSettings.value.grouped_metrics;
});

const isEdit = computed(() => {
  if (countQuery.value > 1) return true;
  return false;
});

const isSeriesEnabled = computed(() => {
  return typeMetric.value !== 'stat' && typeMetric.value !== 'table' && !groupingSwitch.value;
});
//#endregion

//#region methods
const open = () => {
  if (!groupByGrouping.value && groupsAll.value.length) {
    groupByGrouping.value = groupsAll.value[0]!.value;
  }

  if (props.popupType === 'add') {
    handleSelectMetric(columnGroup.value[0]!.items[0]!.name);
  }

  if (props.popupType === 'edit') {
    animation.value = false;
    installConfirmEditMode(props.data!);
    indexEditMetric.value = props.index;
    editModeOpen(props.data!);
  }

  isVisible.value = true;
};

const handleClose = () => {
  if(isEdit.value) {
    const confirmPopup = usePopupManager().confirm.open({
      data: {
        title: t('dashboards.confirm.cancel_title'),
        message: t('dashboards.confirm.cancel_text'),
        confirmTitle: t('dashboards.confirm.cancel_btn_сonfirm'),
        bodyIcon: 'trash',
        confirmIcon: 'trash',
        bodyIconColor: 'var(--text-negative)',
        confirmType: 'negative',
      },
      listeners: {
        success: async () => {
          const context = await confirmPopup;
          emit('close');
          isVisible.value = false;

          context.handleClose();
        },
      },
    });
  } else {
    emit('close');
    isVisible.value = false;
  }
};

const handleSave = () => {
  emit(
    'success',
    props.popupType,
    props.rowIndex,
    dashboardStatComputed.value,
    indexEditMetric.value!,
  );
  isVisible.value = false;
  // handleClose();
};

function installConfirmEditMode(data: DashboardPanel) {
  if (
    data.type === 'chart'
    && typeGuard<DashboardPanelChart>(data)
    && Array.isArray(data.series)
    && data.series.length
    && (data.filters === null || Object.keys(data.filters).length <= 1)
  ) {
    let flag = 1;
    for (let i = 0; i < data.series.length; i += 1) {
      const checkFilter = Object.keys(data.series![i]!.filters!).length;
      if (checkFilter > flag) {
        flag = checkFilter;
      }
    }
    if (flag === 1) countCalcQuery();
  }

  if (
    data.type === 'chart'
    && typeGuard<DashboardPanelChart>(data)
    && data.series_group_by_limit !== null
    && (data.filters === null || Object.keys(data.filters).length <= 1)
  ) {
    countCalcQuery();
  }

  if (
    (data.filters === null || Object.keys(data.filters).length <= 1)
    && data.type !== 'chart'
  ) {
    countCalcQuery();
  }
}

function editModeOpen(data: DashboardPanel) {
  actionsDisabled.value = false;
  if(data.type === 'chart' && typeGuard<DashboardPanelChart>(data)) {
    typeMetric.value = `${data.type}-${data.chart_visualization}`;
  } else {
    typeMetric.value = data.type as DashboardMetricTypes;
  }
  report.value = data.report!;
  forecastValue.value = 'enable_forecast' in data && data.enable_forecast;
  enableComparison.value = 'enable_comparison' in data && data.enable_comparison;
  // проверка на старые метрики
  if (data.filters !== null) {
    filterTab.value = _.cloneDeep(data.filters.search);
    filterSelect.value = _.cloneDeep(data.filters);
    initFiltersDashboard.value = transformForFilterView(decodeFilterSelect(data.filters));
  }

  panelName.value = data.name!;
  if (typeMetric.value === 'stat' && typeGuard<DashboardPanelStat>(data)) {
    selectedMetric.value = data.metric_name;

    // в api таких параметров просто нет
    // dateTypeRange.value = data.current_period;
    // comparedType.value = data.comparison_period;
    nextTick(() => updateStat('getLocalData'));
    return;
  } else if (typeMetric.value === 'table' && typeGuard<DashboardPanelTable>(data)) {
    selectedMetric.value = data.metric_name;
    // в api таких параметров просто нет
    // dateTypeRangeTable.value = data.current_period;
    // comparedType.value = data.comparison_period;

    groupingSwitch.value = data.dimension_name !== null;

    groupByGrouping.value = data.dimension_name!;
    groupingCount.value = data.limit || 5;
    groupingOther.value = data.group_by_add_others!;

    nextTick(() => updateTable('getLocalData'));
  } else if(typeGuard<DashboardPanelChart>(data)) {
    // chartType.value = data.chart_visualization;
    prevChartType.value = chartType.value;
    disabledGroupingSwitch.value = chartType.value === 'pie';
    // параметр period не приходит отсутсвует в api и не приходит с сервера
    // dateTypeRangeChart.value = data.period;
    selectedMetric.value = data.series_group_by_metric_name
      ? data.series_group_by_metric_name
      : data.series!![0]!.metric_name;
    if ((data as DashboardPanelChart).series_group_by_metric_name) {
      groupingSwitch.value = true;
      groupByGrouping.value = data.series_group_by_dimension_name!;
      groupingCount.value = data.series_group_by_limit!;
      groupingOther.value = data.series_group_by_add_others!;
    } else {
      series.value = _.cloneDeep(data.series!);
    }
    nextTick(() => updateChart('getLocalData'));
  }
}

function getTranslateMetrics(metric: string) {
  return formatMetric(reportsSettings.value.metrics.find((e) => e.column_name === metric));
}

async function handleSelectMetric(value?: string) {
  selectedMetric.value = value!;
  actionsDisabled.value = false;
  await getDashboardsStats(paramsData.value);
  updaters[typeMetric.value]('getLocalData');
}

async function changeTabMetric(type: DashboardMetricTypes) {
  loading.value = true;
  disabledGroupingSwitch.value = false;

  if (type === 'chart-pie') {
    disabledGroupingSwitch.value = true;
    groupingSwitch.value = true;
  }

  await changeTypeMetric(type);
}

async function changeTypeMetric(type: DashboardMetricTypes) {
  const isNotValidStatsRequest = isDisableSelectFilter.value && isSeriesEnabled.value;
  if(!isNotValidStatsRequest) {
    await getDashboardsStats(paramsData.value);
  }
  
  updaters[type]('getLocalData');
}

function updateStat(type: string) {
  if (statRef.value) {
    statRef.value.getStat(type);
  }
}

function updateChart(type: string) {
  if (chartRef.value) {
    chartRef.value.getReport(type);
  }
}

function updateTable(type: string) {
  if (tableRef.value) {
    tableRef.value.getReport(type);
  }
}

async function getDashboardsStats(params: PostDashboardsStatsData['body']) {
  errors.value = {};
  countCalcQuery();
  store.commit(`dashboards/${ADD_DASHBOARD_IS_LOADING}`, true);
  try {
    const {
      data: { payload },
    } = await api.dashboardsStats(params);
    report.value = payload;
  } catch (e) {
    if(isErrorResponse(e))
      errors.value = e.data.errors;
  } finally {
    store.commit(`dashboards/${ADD_DASHBOARD_IS_LOADING}`, false);
    loading.value = false;
  }
}

function setReportData(rowIndex: number, type: DashboardPanelTypes, index: number, _report: PostDashboardsStatsResponse['payload']) {
  report.value = _report;
}

function handleChangeFilter(filters: CurrentFilter[]) {
  filterSelect.value = parseFilter(transformFromFilterView(filters));
  changeTypeMetric(typeMetric.value);
}

function handleChangeFilterTab(search: FilterRecord) {
  filterTab.value = search;
  changeTypeMetric(typeMetric.value);
}

async function updateSeries(_series: DashboardsRequestSeries) {
  series.value = _series;
  await changeTypeMetric(typeMetric.value);
}

function handleDisableSelectFilter(value: boolean) {
  isDisableSelectFilter.value = value;
}

const countCalcQuery = _.debounce(function countCalcQuery(count: number = 1) {
  countQuery.value += count;
}, 100);
//#endregion

//#region watch
watch(chartType, async (newVal) => {
  if(typeMetric.value === 'stat' || typeMetric.value === 'table') return;
  animation.value = true;
  countCalcQuery();
  disabledGroupingSwitch.value = false;
  if (newVal === 'pie') {
    groupingSwitch.value = true;
    disabledGroupingSwitch.value = true;
    await getDashboardsStats(paramsData.value);
  } else if (prevChartType.value === 'pie') {
    await getDashboardsStats(paramsData.value);
  }
  prevChartType.value = newVal;
  nextTick(() => updateChart('getLocalData'));
}, {immediate: true});

watch(() => props.propCurrencyCode, (newVal) => {
  currencyCode.value = newVal;
}, {immediate: true});

watch(initFiltersDashboard, (v) => {
  handleDisabled(v);
}, {
  immediate: true,
  deep: true,
});
//#endregion

//#region hooks

//#endregion

defineExpose({
  open,
  handleClose,
});
</script>

<template>
  <Popup
    v-model:is-visible="isVisible"
    class="add-dashboard-metrics-popup"
    :title="$t(`dashboards.other.${popupType === 'add' ? 'add_metric' : 'edit_metric'}`)"
    :width="1300"
    :esc-close="true"
    @close="handleClose"
  >
    <template v-slot:body>
      <div class="add-dashboard-metrics-popup__content">
        <div class="add-dashboard-metrics-popup__content-block t:space-y-16">
          <div class="add-dashboard-metrics-popup__content-title">
            {{ $t('dashboards.popup_column.configure_metric') }}
          </div>
          <UiInput
            :label="$t('dashboards.other.panel_name')"
            autosize
            size="big"
            :placeholder="$t('dashboards.other.panel_name_placeholder')"
            class="t:pr-10"
            type="text"
            :model-value="setNamePanel"
            :error="errors?.panel?.name"
            @update:model-value="(val: any) => {
              panelName = val;
              countCalcQuery();
            }"
          />
          <UiScrollbar
            wrap-style="height: 55vh;"
            :native="false"
            :outside="true"
            view-class="t:space-y-16"
          >
            <div
              class="t:flex"
            >
              <UiTabs
                v-model="typeMetric"
                :list="metricList"
                :label="$t('dashboards.popup_column.view')"
                size="big"
                @change="changeTabMetric"
              />
            </div>
            <SelectMetrics
              v-if="(groupingSwitch)
                || typeMetric === 'stat'
                || typeMetric === 'table'"
              :prop-currency-code="currencyCode"
              :label="$t('dashboards.popup_column.metrics')"
              :model-value="selectedMetric"
              @update:model-value="handleSelectMetric"
            />
            <CardTitle
              v-if="typeMetric === 'stat' || typeMetric === 'table'"
              :title="$t('dashboards.other.enable_comparison')"
              class="add-dashboard-metrics-popup__card-title"
            >
              <UiSwitch
                v-model="enableComparison"
                @change="changeTypeMetric(typeMetric)"
              />
            </CardTitle>
            <CardTitle
              v-if="typeMetric !== 'stat'
                && typeMetric !== 'table'
                && chartType !== 'pie'"
              :title="$t('dashboards.other.forecast')"
              class="add-dashboard-metrics-popup__card-title"
            >
              <UiSwitch
                v-model="forecastValue"
                @change="changeTypeMetric(typeMetric)"
              />
            </CardTitle>
            <FormRow
              v-if="typeMetric !== 'stat' && groupsAll.length"
              :title="$t('dashboards.grouping.grouping')"
              class="add-dashboard-metrics-popup__grouping"
            >
              <template v-slot:action>
                <UiSwitch
                  v-model="groupingSwitch"
                  :disabled="disabledGroupingSwitch || addDashboardIsLoading"
                  @change="changeTypeMetric(typeMetric)"
                />
              </template>
              <div
                v-if="groupingSwitch"
                class="add-dashboard-metrics-popup__grouping-content t:space-x-8"
              >
                <span class="t:whitespace-nowrap">{{ $t('dashboards.grouping.group_by') }}</span>
                <UiSelectElement
                  v-model="groupByGrouping"
                  :list="groupsAll"
                  option-value-param="value"
                  option-name-param="label"
                  option-label-param="label"
                  :width="160"
                  @change="changeTypeMetric(typeMetric)"
                />
                <span class="t:whitespace-nowrap">{{ $t('dashboards.grouping.and_show') }}</span>
                <UiSelectElement
                  v-model="groupingCount"
                  :list="resultCount"
                  :width="100"
                  @change="changeTypeMetric(typeMetric)"
                />
                <span class="t:whitespace-nowrap">{{ $t('dashboards.grouping.result') }}</span>
                <div class="add-dashboard-metrics-popup__vertical" />
                <UiCheckbox
                  v-model="groupingOther"
                  :label="$t('dashboards.grouping.other_value')"
                  @change="changeTypeMetric(typeMetric)"
                />
              </div>
            </FormRow>
            <div class="add-dashboard-metrics-popup__content-title">
              {{ $t('dashboards.dashboards_filters.filter') }}
            </div>
            <FormRow>
              <DashboardFilter
                v-model="initFiltersDashboard"
                :set-current-search="filterTab"
                @change-filter="handleChangeFilter"
                @change-filter-tab="handleChangeFilterTab"
              />
            </FormRow>
            <DashboardSeries
              v-if="isSeriesEnabled"
              :series="computedSeries"
              :disabled="actionsDisabled || isDisabled || addDashboardIsLoading || isDisableSelectFilter"
              :errors="errors?.panel?.series"
              :prop-currency-code="propCurrencyCode"
              @update-series="updateSeries"
              @is-disable-select-filter="handleDisableSelectFilter"
            />
          </UiScrollbar>
        </div>
        <div class="add-dashboard-metrics-popup__content-block t:space-y-16">
          <div class="add-dashboard-metrics-popup__content-title">
            {{ $t('dashboards.popup_column.preview_panel') }}
          </div>
          <UiScrollbar
            wrap-style="height: 55vh;"
            :native="false"
            :outside="true"
          >
            <DashboardMetricStat
              v-if="typeMetric === 'stat'"
              ref="stat"
              class="add-dashboard-metrics-popup__stats"
              :stat="{
                ...dashboardStatComputed as DashboardPanelTable
              }"
              :is-loading="loading"
              :period="period"
              @emit-data-report="setReportData"
            />
            <DashboardMetricChart
              v-if="typeMetric !== 'table' && typeMetric !== 'stat'"
              ref="chart"
              :key="chartType"
              class="add-dashboard-metrics-popup__chart"
              :prop-currency-code="propCurrencyCode"
              :chart="{
                ...dashboardStatComputed
              }"
              preview
              :animation="animation"
              :is-loading="loading"
              :period="period"
              @emit-data-report="setReportData"
            />
            <DashboardMetricTable
              v-if="typeMetric === 'table'"
              ref="table"
              class="add-dashboard-metrics-popup__table"
              :is-loading="loading"
              :table="{
                ...dashboardStatComputed
              }"
              :period="period"
            />
          </UiScrollbar>
        </div>
      </div>
    </template>
    <template v-slot:footer>
      <div class="t:justify-end t:flex t:space-x-10">
        <UiButton
          type="negative"
          size="big"
          icon="times"
          @click="handleClose"
        >
          {{ $t('crm.buttons.cancel') }}
        </UiButton>
        <UiButton
          filled
          size="big"
          icon="check"
          :disabled="actionsDisabled || isDisabled || addDashboardIsLoading || isDisableSelectFilter"
          @click="handleSave"
        >
          {{ $t('crm.buttons.save') }}
        </UiButton>
      </div>
    </template>
  </Popup>
</template>

<style lang="scss">
.add-dashboard-metrics-popup {
  &__content{
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    column-gap: 22px;
    overflow: hidden;
  }
  &__content-title {
    color: var(--text-body);
    font-size: 16px;
    font-weight: 500;
    line-height: normal;
  }
  &__card-title {
    .card-title__content{
      border-radius: 8px;
    }
  }
  &__grouping.form-row {
    padding: 7px 8px;
  }
  &__grouping-content {
    display: flex;
    align-items: center;
    color: var(--text-body);
    font-size: 12px;
    font-weight: 400;
    line-height: normal;
  }
  &__vertical {
    border-radius: 1px;
    background-color: var(--border-default);
    width: 1px;
    height: 16px;
  }
  &__stats {
    width: 260px;
  }
  &__chart {
    .dashboard-chart__forecast {
      display: none;
    }
  }
}
</style>
